import { Clause, ContractSection, Coverage, Identifier, Segment, Subsection } from 'models';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { CSSProperties } from 'react';
import { useClause, useCoverage, useSubsection } from './contractClauses';
import { useContractContext } from './contractContext';
import './contract.css';
import { useTranslationByTarget } from 'components/contractLanguagePage/contractLanguagePage';

type Direction = 'down' | 'up';
type SegmentGetter = (index: number) => JSX.Element;
const segmentKeyKeys = [
    'subsectionIndexId',
    'clauseIndexId',
    'coverageIndexId',
    'index',
    'innerClauseIndexId',
];
export const segmentKey = (
    segment: Segment,
    { index, clauseIndexId }: { index?: number; clauseIndexId?: string } = {}
) =>
    `${
        segment.subsectionIndexId || '' //
    },${
        segment.clauseIndexId || '' //
    },${
        segment.coverageIndexId || '' //
    },${
        index || '' //
    },${
        clauseIndexId || '' //
    }`;

export const itemStyles = (level: number, backgroundColor?: string): CSSProperties => {
    return {
        backgroundColor,
        border: '1px solid #D8D8D8',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        padding: '10px',
        margin: `6px 6px 0px ${25 * level}px`,
    };
};

export const useSegmentRecord = (
    segment: Segment,
    clauseIndexId?: string
): Clause | Subsection | Coverage | undefined => {
    const subsection = useSubsection(segment.subsectionIndexId || '');
    const clause = useClause(segment.clauseIndexId || clauseIndexId || '');
    const coverage = useCoverage(segment.coverageIndexId || '');
    if (clauseIndexId) {
        return clause;
    }
    if (segment.subsectionIndexId) {
        return subsection;
    } else if (segment.clauseIndexId) {
        return clause;
    } else if (segment.coverageIndexId) {
        return coverage;
    }
    return undefined;
};

interface SegmentProps {
    segment: Segment;
    contractSection: ContractSection;
    clauseIndexId?: string;
    segmentIndex?: number;
    index?: number;
    translated?: boolean;
}
export const SegmentItem = ({
    segment,
    clauseIndexId,
    contractSection,
    segmentIndex,
    translated,
}: SegmentProps) => {
    const record = useSegmentRecord(segment, clauseIndexId);
    const translation = useTranslationByTarget(
        record?.id || '',
        record?.__type === 'Clause' ? 'content' : 'name'
    );
    const {
        expCol,
        setExpCol,
        selection,
        contract: { contractSections },
        setContractSections,
    } = useContractContext();
    if (!record) {
        console.warn('empty segment');
        return null;
    }
    const indexId = record.indexId();
    const onDelete = () =>
        setContractSections(
            contractSections.map((contractSection_) => {
                if (contractSection_.sectionIndexId !== contractSection.sectionIndexId) {
                    return contractSection_;
                }
                return {
                    ...contractSection_,
                    segments: clauseIndexId
                        ? contractSection_.segments.map((segment, index) =>
                              index === segmentIndex
                                  ? new Segment({
                                        ...segment,
                                        clauseIndexIds: segment.clauseIndexIds.filter(
                                            (clauseIndexId_) => clauseIndexId_ !== clauseIndexId
                                        ),
                                    })
                                  : segment
                          )
                        : contractSection_.segments.filter((_, index) => index !== segmentIndex),
                };
            })
        );
    return (
        <div>
            <div
                className="segmentItem"
                style={itemStyles(
                    clauseIndexId ? 3 : 2,
                    record.__type === 'Clause' ? undefined : '#F4F4F4'
                )}
            >
                <DragIndicatorIcon
                    htmlColor="#818181"
                    fontSize="small"
                    style={{ marginRight: '10px' }}
                    onClick={() => null}
                />
                {record.__type === 'Clause' ? (
                    <div
                        style={{
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            zIndex: 2,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setExpCol(
                                indexId,
                                expCol.getValue(record.indexId()) === 'expanded'
                                    ? 'collapsed'
                                    : 'expanded'
                            );
                        }}
                    >
                        {expCol.getValue(indexId) === 'expanded' ? (
                            <ArrowDropDownIcon />
                        ) : (
                            <ArrowRightIcon />
                        )}
                    </div>
                ) : null}
                <div
                    style={{
                        flexGrow: 1,
                        color: translated && !translation ? 'red' : 'black',
                    }}
                    id={`#${record.__type}-${record.id}`}
                >
                    {record.__type !== 'Clause' && translated && translation
                        ? translation.content
                        : record.name}
                </div>
                <div className="segmentItemDelete">
                    <div style={{ width: '16px' }}></div>
                    <DeleteOutlineIcon
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                    />
                </div>
            </div>
            {record.__type === 'Clause' && expCol.getValue(indexId) === 'expanded' ? (
                <div
                    style={{
                        border: selection === indexId ? '3px solid #18a0fb' : '1px solid #D8D8D8',
                        borderTopWidth: '0px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                        margin: '0px 6px 0px 75px',
                        whiteSpace: 'break-spaces',
                        color: translated && !translation ? 'red' : 'black',
                    }}
                    onClick={() => null}
                >
                    {translated && translation ? translation.content : record.content}
                </div>
            ) : null}
        </div>
    );
};

const getSegmentListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? 'lightblue' : 'inherit',
});

const getSegmentStyle = (isDragging: boolean, draggableStyle?: CSSProperties): CSSProperties => ({
    userSelect: 'none',
    background: isDragging ? 'lightgreen' : 'inherit',
    ...draggableStyle,
});

const getByIndex = (
    segments: Segment[],
    index: number
): [Segment | undefined, string | undefined, number] => {
    var clausesCounter = 0;
    const segmentIndex = segments.findIndex((segment_, position) => {
        clausesCounter += segment_.clauseIndexIds.length;
        return clausesCounter + position >= index;
    });
    if (segmentIndex === -1) {
        return [undefined, undefined, segmentIndex];
    }
    const segment = segments[segmentIndex];
    const clauseIndex = index - segmentIndex - clausesCounter + segment.clauseIndexIds.length - 1;
    const clauseIndexId = segmentIndex === index ? undefined : segment.clauseIndexIds[clauseIndex];
    return [segment, clauseIndexId, segmentIndex];
};

const getTargetIdentifier = (segments: Segment[], index: number): Identifier | undefined => {
    const [segment, clauseIndexId, segmentIndex] = getByIndex(segments, index);
    if (!segment) {
        return undefined;
    }
    if (clauseIndexId) {
        return { type: 'Inner', indexId: clauseIndexId, segmentIndex };
    }
    if (segment.clauseIndexId) {
        return { type: 'Clause', indexId: segment.clauseIndexId, segmentIndex };
    }
    if (segment.coverageIndexId) {
        return { type: 'Coverage', indexId: segment.coverageIndexId, segmentIndex };
    }
    if (segment.subsectionIndexId) {
        return { type: 'Subsection', indexId: segment.subsectionIndexId, segmentIndex };
    }
    throw new Error('impossible getTargetIdentifier');
};

const withoutIdentifier = (segments: Segment[], identifier: Identifier): Segment[] => {
    return identifier.type === 'Inner'
        ? segments.map(
              (segment) =>
                  new Segment({
                      ...segment,
                      clauseIndexIds: segment.clauseIndexIds.filter(
                          (clauseIndexId) => clauseIndexId !== identifier.indexId
                      ),
                  })
          )
        : segments.filter((segment) => !segment.match(identifier));
};

interface GetCombineTargetIdentifierArgs {
    segments: Segment[];
    target: {
        subsectionIndexId?: string;
        clauseIndexId?: string;
        coverageIndexId?: string;
        index?: string;
        innerClauseIndexId?: string;
    };
}
const getCombineTargetIdentifier = ({
    segments,
    target,
}: GetCombineTargetIdentifierArgs): Identifier => {
    const { subsectionIndexId, clauseIndexId, coverageIndexId, innerClauseIndexId } = target;
    const indexId = subsectionIndexId || clauseIndexId || coverageIndexId || '';
    const data: Identifier = {
        indexId,
        type: subsectionIndexId
            ? 'Subsection'
            : coverageIndexId
            ? 'Coverage'
            : clauseIndexId
            ? 'Clause'
            : 'Inner',
        segmentIndex: 0,
    };
    const segmentIndex = segments.findIndex((segment) => segment.match(data));
    const identifier: Identifier = {
        segmentIndex,
        type: innerClauseIndexId
            ? 'Inner'
            : subsectionIndexId
            ? 'Subsection'
            : coverageIndexId
            ? 'Coverage'
            : 'Clause',
        indexId: innerClauseIndexId || subsectionIndexId || clauseIndexId || coverageIndexId || '',
    };
    return identifier;
};

const onDragEndCombineCalculate = (segments: Segment[], result: DropResult) => {
    const sourceIndex = result.source.index as number;
    const sourceIdentifier = getTargetIdentifier(segments, sourceIndex) as Identifier;
    if (sourceIdentifier.type !== 'Inner' && sourceIdentifier.type !== 'Clause') {
        throw new Error('Combine source must be a clause');
    }
    const values = result.combine?.draggableId.split(',') as string[];
    const target = Object.fromEntries(segmentKeyKeys.map((k, i) => [k, values[i]]));
    const targetIdentifier = getCombineTargetIdentifier({ segments, target });
    if (targetIdentifier.type === 'Clause') {
        throw new Error('Combine target must not be a clause segment');
    }
    const segmentsWithoutInner = withoutIdentifier(segments, sourceIdentifier);
    return segmentsWithoutInner.map((segment, index) => {
        const segmentIndex = targetIdentifier.segmentIndex as number;

        if (
            index !==
            segmentIndex + (sourceIdentifier.segmentIndex < targetIdentifier.segmentIndex ? -1 : 0)
        ) {
            return segment;
        }
        const clauseIndexIds = [...segment.clauseIndexIds];
        const clauseIndex = clauseIndexIds.findIndex(
            (clauseIndexId) => clauseIndexId === targetIdentifier.indexId
        );
        clauseIndexIds.splice(
            targetIdentifier.type === 'Inner' ? 1 + clauseIndex : 0,
            0,
            sourceIdentifier.indexId
        );
        return new Segment({ ...segment, clauseIndexIds });
    });
};

const onDragEndCalculate = (segments: Segment[], result: DropResult) => {
    console.info('result', result);
    if (result.combine) {
        console.info('combine');
        return onDragEndCombineCalculate(segments, result);
    }
    const { destination } = result;
    if (!destination) {
        throw new Error('no destination');
    }
    const sourceIndex = result.source.index as number;
    const targetIndex = destination.index as number;
    const direction: Direction = sourceIndex < targetIndex ? 'down' : 'up';
    const targetRange =
        direction === 'down' ? [targetIndex, targetIndex + 1] : [targetIndex - 1, targetIndex];
    const sourceIdentifier = getTargetIdentifier(segments, sourceIndex) as Identifier;
    const targetrIdentifiers = targetRange.map((index_) => getTargetIdentifier(segments, index_));
    const newSegment =
        sourceIdentifier.type === 'Inner'
            ? new Segment({ clauseIndexId: sourceIdentifier.indexId })
            : new Segment({
                  ...segments.find((_, index) => index === sourceIdentifier.segmentIndex),
              });
    const segmentsWithoutInner = withoutIdentifier(segments, sourceIdentifier);
    if (targetrIdentifiers[1] === undefined) {
        console.info('placed last');
        return segmentsWithoutInner.concat([newSegment]);
    }
    if (targetrIdentifiers[1].type !== 'Inner') {
        console.info('placed before segment');
        const targetIndex = targetrIdentifiers[direction === 'down' ? 0 : 1]
            ?.segmentIndex as number;
        segmentsWithoutInner.splice(targetIndex, 0, newSegment);
        return segmentsWithoutInner;
    } else {
        console.info('placed between inner');
        if (sourceIdentifier.type === 'Coverage' || sourceIdentifier.type === 'Subsection') {
            throw new Error('Can only merge clauses');
        }
        return segmentsWithoutInner.map((segment, index) => {
            const segmentIndex = targetrIdentifiers[0]?.segmentIndex as number;
            const indexToSelect =
                segmentIndex - (direction === 'down' && sourceIdentifier.type !== 'Inner' ? 1 : 0);
            if (index !== indexToSelect) {
                return segment;
            }
            const clauseIndexIds = segment.clauseIndexIds;
            const clauseIndex = segment.clauseIndexIds.findIndex(
                (clauseIndexId) => clauseIndexId === targetrIdentifiers[1]?.indexId
            );
            clauseIndexIds.splice(clauseIndex, 0, sourceIdentifier.indexId);
            return new Segment({ ...segment, clauseIndexIds });
        });
    }
};
interface SegmentsProps {
    segments: Segment[];
    contractSection: ContractSection;
    translated?: boolean;
}
export const Segments = ({ segments, contractSection, translated }: SegmentsProps) => {
    const {
        setContractSections,
        contract: { contractSections },
    } = useContractContext();
    const onChange = (updatedSegments: Segment[]) =>
        setContractSections(
            contractSections.map((contractSection_) =>
                contractSection_.sectionIndexId !== contractSection.sectionIndexId
                    ? contractSection_
                    : { ...contractSection_, segments: updatedSegments }
            )
        );
    const onDragEnd = (result: DropResult) => onChange(onDragEndCalculate(segments, result));

    const segmentItemsArrays = segments.map((segment, segmentIndex) => [
        (index: number) => (
            <Draggable
                key={segmentKey(segment, { index })}
                draggableId={segmentKey(segment, { index })}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getSegmentStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <SegmentItem
                            index={index}
                            segment={segment}
                            contractSection={contractSection}
                            segmentIndex={segmentIndex}
                            translated={translated}
                        />
                    </div>
                )}
            </Draggable>
        ),
        ...segment.getClauses().map((clauseIndexId) => (index: number) => (
            <Draggable
                key={clauseIndexId}
                draggableId={segmentKey(segment, { clauseIndexId })}
                index={index}
            >
                {(provided, snapshot) => (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getSegmentStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <SegmentItem
                            index={index}
                            segment={segment}
                            segmentIndex={segmentIndex}
                            clauseIndexId={clauseIndexId}
                            contractSection={contractSection}
                            translated={translated}
                        />
                    </div>
                )}
            </Draggable>
        )),
    ]);
    const segmentItemGetters = ([] as SegmentGetter[]).concat(...segmentItemsArrays);
    if (segments.length) {
        return (
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                    droppableId={`droppable-${contractSection.sectionIndexId}`}
                    isCombineEnabled={true}
                >
                    {(provided, snapshot) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getSegmentListStyle(snapshot.isDraggingOver)}
                        >
                            {segmentItemGetters.map((getSegment, index) => getSegment(index))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        );
    } else {
        return <div style={{ height: '6px' }}></div>;
    }
};
